import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, Copy, Terminal } from "lucide-react";
import { cn } from "@/lib/utils";

export interface CommandCopyBoxProps {
  /** Label rendered next to the Terminal icon. */
  label: string;
  /** The command string displayed in the monospace box. */
  command: string;
  /** Caption shown below the command box. */
  helperText: string;
  /** data-help-id applied to the <code> element. */
  commandHelpId?: string;
  /** data-help-id applied to the copy button. */
  copyButtonHelpId?: string;
  /** Extra classes on the outer container. */
  className?: string;
  /** Visual tone for the command surface. */
  theme?: "dark" | "light";
}

export function CommandCopyBox({
  label,
  command,
  helperText,
  commandHelpId,
  copyButtonHelpId,
  className,
  theme = "dark",
}: CommandCopyBoxProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLight = theme === "light";

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = useCallback(async () => {
    let success = false;
    try {
      await navigator.clipboard.writeText(command);
      success = true;
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = command;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      try {
        textArea.select();
        const copyFallback = Reflect.get(document as object, "execCommand") as
          | ((command: string) => boolean)
          | undefined;
        if (typeof copyFallback === "function") {
          success = copyFallback.call(document, "copy");
        }
      } finally {
        document.body.removeChild(textArea);
      }
    }
    if (!success) return;
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setCopied(false);
      timerRef.current = null;
    }, 2000);
  }, [command]);

  return (
    <div className={cn("space-y-2", className)}>
      <div
        className={cn(
          "flex items-center gap-2 text-sm font-medium",
          isLight ? "text-gray-600" : "text-muted-foreground"
        )}
      >
        <Terminal className="h-4 w-4" />
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <code
          className={cn(
            "flex-1 overflow-x-auto rounded-2xl border px-4 py-3 text-sm font-mono select-all",
            isLight
              ? "border-gray-200 bg-gray-50 text-gray-900"
              : "border-white/[0.06] bg-white/[0.03]"
          )}
          {...(commandHelpId ? { "data-help-id": commandHelpId } : {})}
        >
          {command}
        </code>
        <Button
          variant="outline"
          size="icon"
          className={cn(
            "h-11 w-11 shrink-0 rounded-2xl",
            isLight &&
              "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-100 hover:text-gray-900"
          )}
          aria-label={copied ? "Copied command" : "Copy command"}
          onClick={() => {
            void handleCopy();
          }}
          {...(copyButtonHelpId ? { "data-help-id": copyButtonHelpId } : {})}
        >
          {copied ? (
            <Check className={cn("h-4 w-4", isLight ? "text-emerald-600" : "text-green-600")} />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
      </div>
      <p className={cn("text-xs", isLight ? "text-gray-500" : "text-muted-foreground")}>
        {helperText}
      </p>
    </div>
  );
}
